// src/components/PrintPreview.jsx
import React, { useCallback, useEffect } from 'react';
import { viewImage } from '../CommonUtils/viewImage';

/* ═══════════════════════════════════════════════
   UTILITY FUNCTIONS
═══════════════════════════════════════════════ */
export const safeArray = (val) => (Array.isArray(val) ? val : []);
export const dedup = (arr, key = "sno") =>
    Array.from(new Map(arr.map((item, i) => [item[key] ?? i, item])).values());
export const fmt = (v) => (v !== null && v !== undefined && v !== "" ? String(v) : null);

export const formatYesNo = (val) => {
    if (val === true || val === "true") return "Yes";
    if (val === false || val === "false") return "No";
    return val ?? "-";
};

export const formatBuildOwnLease = (value) => {
    if (value === true || value === "true") return "Own";
    if (value === false || value === "false") return "Leased";
    return value;
};

/* ═══════════════════════════════════════════════
   PRINT HOOK
═══════════════════════════════════════════════ */
export const usePrintHandler = (options = {}) => {
    const {
        margin = '10mm 8mm',
        size = 'A4 portrait',
        additionalStyles = ''
    } = options;

    const handlePrint = useCallback(() => {
        const style = document.createElement("style");
        style.id = "__pvp_temp_print";
        style.innerHTML = `
      @page { 
        margin: ${margin}; 
        size: ${size}; 
      }
      ${additionalStyles}
    `;
        document.head.appendChild(style);

        window.print();

        window.addEventListener(
            "afterprint",
            () => document.getElementById("__pvp_temp_print")?.remove(),
            { once: true }
        );
    }, [margin, size, additionalStyles]);

    return { handlePrint };
};

/* ═══════════════════════════════════════════════
   PRINT STYLES COMPONENT
═══════════════════════════════════════════════ */
export function PrintStyles() {
    useEffect(() => {
        const existing = document.getElementById("__pvp_print_styles");
        if (existing) return;
        const link = document.createElement("link");
        link.id = "__pvp_print_styles";
        link.rel = "stylesheet";
        link.href = "/src/styles/PrintStyles.css";
        document.head.appendChild(link);
        return () => {
            document.getElementById("__pvp_print_styles")?.remove();
        };
    }, []);
    return null;
}

/* ═══════════════════════════════════════════════
   PRINT HEADER COMPONENT
═══════════════════════════════════════════════ */
export function PrintHeader({ onPrint, title = "Preview & Print" }) {
    return (
        <div className="pvp-header">
            {title && <h2 style={{ margin: 0 }}>{title}</h2>}
            <button className="pvp-print-btn" onClick={onPrint}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}>
                    <polyline points="6 9 6 2 18 2 18 9" />
                    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                    <rect x="6" y="14" width="12" height="8" />
                </svg>
                Print / Download Preview
            </button>
        </div>
    );
}

/* ═══════════════════════════════════════════════
   RENDER VALUE HELPER - Accepts fileFields as parameter
═══════════════════════════════════════════════ */
export function renderValue({ key, value, valueMaps, fileFields = [] }) {
    const isFileField = fileFields.includes(key);
    const rawVal = fmt(value);
    const normalized = String(rawVal).toLowerCase();

    if (isFileField && value) {
        return (
            <button className="pvp-doc-view-btn" onClick={() => viewImage(value)}>
                <i className="fa fa-eye"></i> View
            </button>
        );
    }

    if (valueMaps?.[key]) {
        return valueMaps[key][value] || "—";
    }

    if (normalized === "true") return "Yes";
    if (normalized === "false") return "No";
    return rawVal ?? "—";
}

/* ═══════════════════════════════════════════════
   FIELD GRID COMPONENT - Accepts fileFields as parameter
═══════════════════════════════════════════════ */
export function FieldGrid({ data, fields, valueMaps = {}, fileFields = [] }) {
    const visible = fields.filter((f) => {
        const val = fmt(data[f.key]) ?? (f.fallbackKey ? fmt(data[f.fallbackKey]) : null);
        return val !== null;
    });

    if (!visible.length) {
        return <div className="pvp-empty">No data available.</div>;
    }

    return (
        <div className="pvp-kv-grid">
            {visible.map(({ key, fallbackKey, label }) => {
                const value = data[key] ?? (fallbackKey ? data[fallbackKey] : null);
                return (
                    <div className="pvp-kv-item" key={key}>
                        <span className="pvp-kv-key">{label}</span>
                        <span className="pvp-kv-val">
                            {renderValue({ key, value, valueMaps, fileFields })}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}

/* ═══════════════════════════════════════════════
   ARRAY TABLE COMPONENT - Accepts fileFields as parameter
═══════════════════════════════════════════════ */
export function DataTable({ rows = [], columns = [], valueMaps = {}, fileFields = [] }) {
    if (!rows.length) {
        return <div className="pvp-empty">No records found.</div>;
    }

    return (
        <div className="pvp-table-wrap">
            <table className="pvp-table">
                <thead>
                    <tr>
                        <th>S.No</th>
                        {columns.map((c) => (
                            <th key={c.key}>{c.label}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, i) => (
                        <tr key={i}>
                            <td>{i + 1}</td>
                            {columns.map((c) => (
                                <td key={c.key}>
                                    {renderValue({
                                        key: c.key,
                                        value: row?.[c.key],
                                        valueMaps,
                                        fileFields
                                    })}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/* ═══════════════════════════════════════════════
   SECTION HEADER COMPONENT
═══════════════════════════════════════════════ */
export function SectionHeader({ icon, title, color, bg, count }) {
    return (
        <div className="pvp-sec-hdr">
            <div className="pvp-sec-icon" style={{ background: bg, color }}>
                {icon}
            </div>
            <span className="pvp-sec-title">{title}</span>
            {count !== undefined && (
                <span className="pvp-sec-badge" style={{ background: bg, color }}>
                    {count} {count === 1 ? "record" : "records"}
                </span>
            )}
        </div>
    );
}